/**
 * F-072 — Translation catalogs for 10 supported locales.
 *
 * Each locale must define the same key set (validated at runtime by tests).
 * Strings using `{var}` are interpolated by `t()`.
 *
 * Naming:
 *   score.label / score.high
 *   queue.next
 *   integrity.label
 *   pause.label
 *   goal.label / goal.peak (with {current}/{total})
 *   combo.line  (with {n}/{m})
 *   breach.danger / breach.atmosphere / breach.escape
 *   gameover.shattered.title / .sub / .cta
 *   gameover.star.title / .sub / .cta
 *   gameover.final / gameover.high  (with {value})
 *   tutorial.skip / .next / .start
 *   tutorial.step.launch.title / .body
 *   tutorial.step.merge.title / .body
 *   tutorial.step.danger.title / .body
 *   tutorial.step.goal.title / .body
 *   tier.<machine-name>.name (× 9)
 */

import type { Locale } from "./index.ts";

export type Catalog = Record<string, string>;

const ko: Catalog = {
  "score.label": "점수 ·",
  "score.high": "최고 ·",
  "queue.next": "다음",
  "integrity.label": "지구 내구도",
  "pause.label": "일시정지",
  "goal.label": "🎯 목표 · 별 탄생시키기",
  "goal.peak": "최고 단계 {current} / {total}",
  "goal.peakDot": "최고 단계 · {current} / {total}",
  "combo.line": "콤보 ×{n} · {m}연쇄",
  "breach.danger": "위험!",
  "breach.atmosphere": "⚠ 대기권 돌파!",
  "breach.escape": "🌌 궤도 이탈!",
  "gameover.shattered.title": "💥 지구 붕괴",
  "gameover.shattered.sub":
    "대기권 한계선을 넘은 잔해가 너무 많이 쌓여 지구가 무너졌습니다.",
  "gameover.shattered.cta": "다시 시작",
  "gameover.star.title": "🌟 별 탄생",
  "gameover.star.sub":
    "별을 만들어냈습니다! 무한 모드 활성화 — 최고 점수를 노려보세요.",
  "gameover.star.cta": "계속하기",
  "gameover.final": "최종 · {value}",
  "gameover.high": "최고 · {value}",
  "tutorial.skip": "건너뛰기",
  "tutorial.next": "다음",
  "tutorial.start": "시작!",
  "tutorial.step.launch.title": "🌌 발사",
  "tutorial.step.launch.body":
    "화면을 누르고 슬링샷 반대 방향으로 끌었다가 놓으면 운석이 발사됩니다. 강하게 끌수록 더 멀리 날아갑니다.",
  "tutorial.step.merge.title": "✨ 합치기",
  "tutorial.step.merge.body":
    "같은 단계의 운석 두 개가 부딪히면 다음 단계로 합쳐집니다. 운석조각 → 작은운석 → 운석 → … → 별!",
  "tutorial.step.danger.title": "⚠ 주의",
  "tutorial.step.danger.body":
    "운석이 대기권 점선 밖에 머물면 지구가 무너집니다. 큰 운석일수록 피해가 커요. 천천히 공전하는 달도 운석 진로를 막을 수 있습니다.",
  "tutorial.step.goal.title": "🎯 목표 · 별 탄생",
  "tutorial.step.goal.body":
    "9단계 별을 만들면 클리어! 지구를 구하면서 최고 점수에 도전하세요. 행운을 빕니다 ✨",
  "tier.meteor-shard.name": "운석조각",
  "tier.small-meteor.name": "작은운석",
  "tier.meteor.name": "운석",
  "tier.comet.name": "혜성",
  "tier.asteroid.name": "소행성",
  "tier.moon.name": "위성",
  "tier.planet.name": "행성",
  "tier.protostar.name": "원시별",
  "tier.star.name": "별",
  "auth.signin": "Google 로그인",
  "auth.signout": "로그아웃",
  "leaderboard.title": "🏆 글로벌 순위",
  "leaderboard.refresh": "새로고침",
  "leaderboard.close": "닫기",
  "leaderboard.openButton": "순위",
  "leaderboard.empty": "아직 등록된 점수가 없습니다.",
  "leaderboard.loading": "순위를 불러오는 중...",
  "leaderboard.offline": "글로벌 순위는 오프라인입니다 — 로컬 기록만 표시됩니다.",
  "leaderboard.error": "순위 정보를 불러올 수 없습니다.",
  "leaderboard.yourRank": "내 순위",
  "submit.headline": "{value} 점 — 글로벌 순위에 등록하시겠어요?",
  "submit.headlineStar": "🌟 별 탄생! {value} 점을 글로벌 순위에 등록하시겠어요?",
  "submit.signedIn": "{name} 으로 등록됩니다",
  "submit.signedOut": "글로벌 순위에 등록하려면 Google 로그인이 필요합니다.",
  "submit.offline": "글로벌 순위는 현재 비활성화되어 있습니다.",
  "submit.cta": "등록",
  "submit.ctaSignIn": "Google 로그인",
  "submit.submitting": "등록 중...",
  "submit.dismiss": "나중에",
  "submit.close": "닫기",
  "submit.error": "등록에 실패했습니다. 잠시 후 다시 시도해주세요.",
  "gameover.submit": "🏆 글로벌 등록",
  "profile.nicknameEmpty": "닉네임을 입력해주세요.",
  "profile.nicknameTooLong": "닉네임은 20자 이하여야 합니다.",
  "profile.nicknameError": "닉네임 변경에 실패했습니다.",
  "leaderboard.mergesShort": "{n}합",
};

const en: Catalog = {
  "score.label": "Score ·",
  "score.high": "Best ·",
  "queue.next": "Next",
  "integrity.label": "Earth Integrity",
  "pause.label": "Paused",
  "goal.label": "🎯 Goal · Forge a Star",
  "goal.peak": "Peak Tier {current} / {total}",
  "goal.peakDot": "Peak Tier · {current} / {total}",
  "combo.line": "Combo ×{n} · {m} chain",
  "breach.danger": "DANGER!",
  "breach.atmosphere": "⚠ Atmosphere Breach!",
  "breach.escape": "🌌 Orbit Lost!",
  "gameover.shattered.title": "💥 Earth Shattered",
  "gameover.shattered.sub":
    "Too much debris piled past the atmosphere — Earth has collapsed.",
  "gameover.shattered.cta": "Restart",
  "gameover.star.title": "🌟 Star Forged",
  "gameover.star.sub":
    "You forged a star! Endless mode unlocked — chase the high score.",
  "gameover.star.cta": "Continue",
  "gameover.final": "Final · {value}",
  "gameover.high": "Best · {value}",
  "tutorial.skip": "Skip",
  "tutorial.next": "Next",
  "tutorial.start": "Start!",
  "tutorial.step.launch.title": "🌌 Launch",
  "tutorial.step.launch.body":
    "Press, drag away from your target, and release — the meteor flies opposite the slingshot. Pull harder for more range.",
  "tutorial.step.merge.title": "✨ Merge",
  "tutorial.step.merge.body":
    "Two meteors of the same tier collide and merge into the next. Shard → Small Meteor → Meteor → … → Star!",
  "tutorial.step.danger.title": "⚠ Beware",
  "tutorial.step.danger.body":
    "Meteors that linger outside the dotted atmosphere damage Earth — bigger meteors hurt more. The slow-orbiting Moon can also block your shot.",
  "tutorial.step.goal.title": "🎯 Goal · Forge a Star",
  "tutorial.step.goal.body":
    "Reach tier 9 to win! Save Earth and chase the high score. Good luck ✨",
  "tier.meteor-shard.name": "Meteor Shard",
  "tier.small-meteor.name": "Small Meteor",
  "tier.meteor.name": "Meteor",
  "tier.comet.name": "Comet",
  "tier.asteroid.name": "Asteroid",
  "tier.moon.name": "Moon",
  "tier.planet.name": "Planet",
  "tier.protostar.name": "Protostar",
  "tier.star.name": "Star",
  "auth.signin": "Sign in with Google",
  "auth.signout": "Sign out",
  "leaderboard.title": "🏆 Global Leaderboard",
  "leaderboard.refresh": "Refresh",
  "leaderboard.close": "Close",
  "leaderboard.openButton": "Ranks",
  "leaderboard.empty": "No scores yet. Be the first!",
  "leaderboard.loading": "Loading rankings...",
  "leaderboard.offline": "Global ranks are offline — showing local records only.",
  "leaderboard.error": "Could not load rankings.",
  "leaderboard.yourRank": "Your rank",
  "submit.headline": "{value} points — submit to the global leaderboard?",
  "submit.headlineStar": "🌟 Star forged! Submit {value} points to the global leaderboard?",
  "submit.signedIn": "Submitting as {name}",
  "submit.signedOut": "Sign in with Google to register your score.",
  "submit.offline": "Global leaderboard is currently unavailable.",
  "submit.cta": "Submit",
  "submit.ctaSignIn": "Sign in with Google",
  "submit.submitting": "Submitting…",
  "submit.dismiss": "Maybe later",
  "submit.close": "Close",
  "submit.error": "Submission failed. Please try again shortly.",
  "gameover.submit": "🏆 Submit Score",
  "profile.nicknameEmpty": "Please enter a nickname.",
  "profile.nicknameTooLong": "Nickname must be 20 characters or fewer.",
  "profile.nicknameError": "Failed to update nickname.",
  "leaderboard.mergesShort": "{n}m",
};

const ja: Catalog = {
  "score.label": "スコア ·",
  "score.high": "最高 ·",
  "queue.next": "次",
  "integrity.label": "地球の耐久度",
  "pause.label": "一時停止",
  "goal.label": "🎯 目標 · 星を生み出せ",
  "goal.peak": "最高ティア {current} / {total}",
  "goal.peakDot": "最高ティア · {current} / {total}",
  "combo.line": "コンボ ×{n} · {m}連鎖",
  "breach.danger": "危険!",
  "breach.atmosphere": "⚠ 大気圏突破!",
  "breach.escape": "🌌 軌道離脱!",
  "gameover.shattered.title": "💥 地球崩壊",
  "gameover.shattered.sub":
    "オゾン層を越えた残骸が積み重なり、地球が崩壊しました。",
  "gameover.shattered.cta": "リスタート",
  "gameover.star.title": "🌟 星誕生",
  "gameover.star.sub":
    "星を生み出した!エンドレスモード解放 — ハイスコアを狙え。",
  "gameover.star.cta": "続ける",
  "gameover.final": "最終 · {value}",
  "gameover.high": "最高 · {value}",
  "tutorial.skip": "スキップ",
  "tutorial.next": "次へ",
  "tutorial.start": "スタート!",
  "tutorial.step.launch.title": "🌌 発射",
  "tutorial.step.launch.body":
    "画面を押して目標と反対側へドラッグして離すと、隕石はスリングショットの反対方向へ飛びます。強く引くほど遠くへ。",
  "tutorial.step.merge.title": "✨ マージ",
  "tutorial.step.merge.body":
    "同じティアの隕石が衝突すると次のティアへ合体します。隕石片 → 小隕石 → 隕石 → … → 星!",
  "tutorial.step.danger.title": "⚠ 注意",
  "tutorial.step.danger.body":
    "破線の大気圏外に隕石が留まると地球が崩壊します。大きい隕石ほど被害が大きく、ゆっくり公転する月も進路を遮ります。",
  "tutorial.step.goal.title": "🎯 目標 · 星誕生",
  "tutorial.step.goal.body":
    "ティア9の星を作ればクリア!地球を守りつつハイスコアに挑戦しましょう。幸運を ✨",
  "tier.meteor-shard.name": "隕石片",
  "tier.small-meteor.name": "小隕石",
  "tier.meteor.name": "隕石",
  "tier.comet.name": "彗星",
  "tier.asteroid.name": "小惑星",
  "tier.moon.name": "衛星",
  "tier.planet.name": "惑星",
  "tier.protostar.name": "原始星",
  "tier.star.name": "星",
  "auth.signin": "Google でログイン",
  "auth.signout": "ログアウト",
  "leaderboard.title": "🏆 グローバルランキング",
  "leaderboard.refresh": "更新",
  "leaderboard.close": "閉じる",
  "leaderboard.openButton": "ランキング",
  "leaderboard.empty": "まだスコアが登録されていません。",
  "leaderboard.loading": "ランキングを読み込み中…",
  "leaderboard.offline": "グローバルランキングはオフラインです — ローカル記録のみ表示。",
  "leaderboard.error": "ランキングを読み込めません。",
  "leaderboard.yourRank": "あなたの順位",
  "submit.headline": "{value} 点 — グローバルランキングに登録しますか?",
  "submit.headlineStar": "🌟 星誕生! {value} 点をグローバルランキングに登録しますか?",
  "submit.signedIn": "{name} として登録されます",
  "submit.signedOut": "登録するには Google ログインが必要です。",
  "submit.offline": "グローバルランキングは現在無効です。",
  "submit.cta": "登録",
  "submit.ctaSignIn": "Google でログイン",
  "submit.submitting": "登録中…",
  "submit.dismiss": "後で",
  "submit.close": "閉じる",
  "submit.error": "登録に失敗しました。しばらく後に再試行してください。",
  "gameover.submit": "🏆 スコア登録",
  "profile.nicknameEmpty": "ニックネームを入力してください。",
  "profile.nicknameTooLong": "ニックネームは20文字以内で入力してください。",
  "profile.nicknameError": "ニックネームの更新に失敗しました。",
  "leaderboard.mergesShort": "{n}合",
};

const zhCN: Catalog = {
  "score.label": "得分 ·",
  "score.high": "最高 ·",
  "queue.next": "下一个",
  "integrity.label": "地球完整度",
  "pause.label": "已暂停",
  "goal.label": "🎯 目标 · 锻造一颗星",
  "goal.peak": "最高阶 {current} / {total}",
  "goal.peakDot": "最高阶 · {current} / {total}",
  "combo.line": "连击 ×{n} · {m} 连",
  "breach.danger": "危险!",
  "breach.atmosphere": "⚠ 大气层突破!",
  "breach.escape": "🌌 偏离轨道!",
  "gameover.shattered.title": "💥 地球崩塌",
  "gameover.shattered.sub":
    "穿过臭氧层的碎片堆积过多 — 地球崩塌了。",
  "gameover.shattered.cta": "重新开始",
  "gameover.star.title": "🌟 恒星诞生",
  "gameover.star.sub":
    "你锻造了一颗星!无尽模式已解锁 — 挑战最高分。",
  "gameover.star.cta": "继续",
  "gameover.final": "最终 · {value}",
  "gameover.high": "最高 · {value}",
  "tutorial.skip": "跳过",
  "tutorial.next": "下一步",
  "tutorial.start": "开始!",
  "tutorial.step.launch.title": "🌌 发射",
  "tutorial.step.launch.body":
    "按下并向目标的反方向拖动后松开,陨石将沿弹弓的反方向飞出。拉得越大,飞得越远。",
  "tutorial.step.merge.title": "✨ 合成",
  "tutorial.step.merge.body":
    "两颗同阶陨石碰撞后合成下一阶。陨石碎片 → 小陨石 → 陨石 → … → 星!",
  "tutorial.step.danger.title": "⚠ 注意",
  "tutorial.step.danger.body":
    "陨石停留在虚线大气层外会使地球崩塌。陨石越大伤害越大,缓慢公转的月球也会阻挡轨迹。",
  "tutorial.step.goal.title": "🎯 目标 · 恒星诞生",
  "tutorial.step.goal.body":
    "锻造第 9 阶之星即可通关!保护地球的同时挑战最高分。祝你好运 ✨",
  "tier.meteor-shard.name": "陨石碎片",
  "tier.small-meteor.name": "小陨石",
  "tier.meteor.name": "陨石",
  "tier.comet.name": "彗星",
  "tier.asteroid.name": "小行星",
  "tier.moon.name": "卫星",
  "tier.planet.name": "行星",
  "tier.protostar.name": "原恒星",
  "tier.star.name": "星",
  "auth.signin": "使用 Google 登录",
  "auth.signout": "退出",
  "leaderboard.title": "🏆 全球排行榜",
  "leaderboard.refresh": "刷新",
  "leaderboard.close": "关闭",
  "leaderboard.openButton": "排行",
  "leaderboard.empty": "还没有任何分数。",
  "leaderboard.loading": "正在加载排行...",
  "leaderboard.offline": "全球排行榜处于离线状态 — 仅显示本地记录。",
  "leaderboard.error": "无法加载排行。",
  "leaderboard.yourRank": "我的排名",
  "submit.headline": "{value} 分 — 提交到全球排行榜?",
  "submit.headlineStar": "🌟 恒星诞生!将 {value} 分提交到全球排行榜?",
  "submit.signedIn": "将以 {name} 提交",
  "submit.signedOut": "提交分数需要使用 Google 登录。",
  "submit.offline": "全球排行榜当前不可用。",
  "submit.cta": "提交",
  "submit.ctaSignIn": "使用 Google 登录",
  "submit.submitting": "提交中…",
  "submit.dismiss": "稍后",
  "submit.close": "关闭",
  "submit.error": "提交失败,请稍后重试。",
  "gameover.submit": "🏆 提交分数",
  "profile.nicknameEmpty": "请输入昵称。",
  "profile.nicknameTooLong": "昵称不能超过20个字符。",
  "profile.nicknameError": "昵称更新失败。",
  "leaderboard.mergesShort": "{n}合",
};

const zhTW: Catalog = {
  "score.label": "得分 ·",
  "score.high": "最高 ·",
  "queue.next": "下一個",
  "integrity.label": "地球完整度",
  "pause.label": "已暫停",
  "goal.label": "🎯 目標 · 鍛造一顆星",
  "goal.peak": "最高階 {current} / {total}",
  "goal.peakDot": "最高階 · {current} / {total}",
  "combo.line": "連擊 ×{n} · {m} 連",
  "breach.danger": "危險!",
  "breach.atmosphere": "⚠ 大氣層突破!",
  "breach.escape": "🌌 偏離軌道!",
  "gameover.shattered.title": "💥 地球崩塌",
  "gameover.shattered.sub":
    "穿過臭氧層的碎片堆積過多 — 地球崩塌了。",
  "gameover.shattered.cta": "重新開始",
  "gameover.star.title": "🌟 恆星誕生",
  "gameover.star.sub":
    "你鍛造了一顆星!無盡模式已解鎖 — 挑戰最高分。",
  "gameover.star.cta": "繼續",
  "gameover.final": "最終 · {value}",
  "gameover.high": "最高 · {value}",
  "tutorial.skip": "跳過",
  "tutorial.next": "下一步",
  "tutorial.start": "開始!",
  "tutorial.step.launch.title": "🌌 發射",
  "tutorial.step.launch.body":
    "按下並向目標的反方向拖曳後放開,隕石將沿彈弓的反方向飛出。拉得越大,飛得越遠。",
  "tutorial.step.merge.title": "✨ 合成",
  "tutorial.step.merge.body":
    "兩顆同階隕石碰撞後合成下一階。隕石碎片 → 小隕石 → 隕石 → … → 星!",
  "tutorial.step.danger.title": "⚠ 注意",
  "tutorial.step.danger.body":
    "隕石停留在虛線大氣層外會使地球崩塌。隕石越大傷害越大,緩慢公轉的月球也會阻擋軌跡。",
  "tutorial.step.goal.title": "🎯 目標 · 恆星誕生",
  "tutorial.step.goal.body":
    "鍛造第 9 階之星即可通關!保護地球的同時挑戰最高分。祝你好運 ✨",
  "tier.meteor-shard.name": "隕石碎片",
  "tier.small-meteor.name": "小隕石",
  "tier.meteor.name": "隕石",
  "tier.comet.name": "彗星",
  "tier.asteroid.name": "小行星",
  "tier.moon.name": "衛星",
  "tier.planet.name": "行星",
  "tier.protostar.name": "原恆星",
  "tier.star.name": "星",
  "auth.signin": "使用 Google 登入",
  "auth.signout": "登出",
  "leaderboard.title": "🏆 全球排行榜",
  "leaderboard.refresh": "重新整理",
  "leaderboard.close": "關閉",
  "leaderboard.openButton": "排行",
  "leaderboard.empty": "還沒有任何分數。",
  "leaderboard.loading": "正在載入排行...",
  "leaderboard.offline": "全球排行榜處於離線狀態 — 僅顯示本地紀錄。",
  "leaderboard.error": "無法載入排行。",
  "leaderboard.yourRank": "我的排名",
  "submit.headline": "{value} 分 — 提交到全球排行榜?",
  "submit.headlineStar": "🌟 恆星誕生!將 {value} 分提交到全球排行榜?",
  "submit.signedIn": "將以 {name} 提交",
  "submit.signedOut": "提交分數需要使用 Google 登入。",
  "submit.offline": "全球排行榜目前無法使用。",
  "submit.cta": "提交",
  "submit.ctaSignIn": "使用 Google 登入",
  "submit.submitting": "提交中…",
  "submit.dismiss": "稍後",
  "submit.close": "關閉",
  "submit.error": "提交失敗,請稍後重試。",
  "gameover.submit": "🏆 提交分數",
  "profile.nicknameEmpty": "請輸入暱稱。",
  "profile.nicknameTooLong": "暱稱不能超過 20 個字元。",
  "profile.nicknameError": "暱稱更新失敗。",
  "leaderboard.mergesShort": "{n}合",
};

const es: Catalog = {
  "score.label": "Puntos ·",
  "score.high": "Récord ·",
  "queue.next": "Siguiente",
  "integrity.label": "Integridad de la Tierra",
  "pause.label": "Pausa",
  "goal.label": "🎯 Objetivo · Forjar una Estrella",
  "goal.peak": "Nivel máx. {current} / {total}",
  "goal.peakDot": "Nivel máx. · {current} / {total}",
  "combo.line": "Combo ×{n} · cadena {m}",
  "breach.danger": "¡PELIGRO!",
  "breach.atmosphere": "⚠ ¡Brecha en la atmósfera!",
  "breach.escape": "🌌 ¡Órbita perdida!",
  "gameover.shattered.title": "💥 Tierra destruida",
  "gameover.shattered.sub":
    "Demasiados restos cruzaron la ozonosfera — la Tierra colapsó.",
  "gameover.shattered.cta": "Reiniciar",
  "gameover.star.title": "🌟 Estrella forjada",
  "gameover.star.sub":
    "¡Forjaste una estrella! Modo infinito desbloqueado — busca el récord.",
  "gameover.star.cta": "Continuar",
  "gameover.final": "Final · {value}",
  "gameover.high": "Récord · {value}",
  "tutorial.skip": "Omitir",
  "tutorial.next": "Siguiente",
  "tutorial.start": "¡Empezar!",
  "tutorial.step.launch.title": "🌌 Lanzamiento",
  "tutorial.step.launch.body":
    "Pulsa, arrastra en sentido opuesto al objetivo y suelta — el meteorito sale en dirección contraria a la honda. Cuanto más tires, más lejos llega.",
  "tutorial.step.merge.title": "✨ Fusión",
  "tutorial.step.merge.body":
    "Dos meteoritos del mismo nivel chocan y se funden en el siguiente. ¡Fragmento → Pequeño → Meteorito → … → Estrella!",
  "tutorial.step.danger.title": "⚠ Cuidado",
  "tutorial.step.danger.body":
    "Los meteoritos que se quedan fuera de la atmósfera punteada dañan la Tierra; cuanto mayores, peor. La Luna en órbita lenta también puede bloquear el tiro.",
  "tutorial.step.goal.title": "🎯 Objetivo · Forjar una estrella",
  "tutorial.step.goal.body":
    "¡Alcanza el nivel 9 para ganar! Salva la Tierra y persigue el récord. ¡Buena suerte! ✨",
  "tier.meteor-shard.name": "Fragmento",
  "tier.small-meteor.name": "Meteorito pequeño",
  "tier.meteor.name": "Meteorito",
  "tier.comet.name": "Cometa",
  "tier.asteroid.name": "Asteroide",
  "tier.moon.name": "Luna",
  "tier.planet.name": "Planeta",
  "tier.protostar.name": "Protoestrella",
  "tier.star.name": "Estrella",
  "auth.signin": "Iniciar sesión con Google",
  "auth.signout": "Cerrar sesión",
  "leaderboard.title": "🏆 Clasificación global",
  "leaderboard.refresh": "Actualizar",
  "leaderboard.close": "Cerrar",
  "leaderboard.openButton": "Ranking",
  "leaderboard.empty": "Aún no hay puntuaciones. ¡Sé el primero!",
  "leaderboard.loading": "Cargando ranking…",
  "leaderboard.offline": "El ranking global está fuera de línea — solo se muestran récords locales.",
  "leaderboard.error": "No se pudo cargar el ranking.",
  "leaderboard.yourRank": "Tu posición",
  "submit.headline": "{value} puntos — ¿enviar al ranking global?",
  "submit.headlineStar": "🌟 ¡Estrella forjada! ¿Enviar {value} puntos al ranking global?",
  "submit.signedIn": "Se enviará como {name}",
  "submit.signedOut": "Inicia sesión con Google para registrar tu puntuación.",
  "submit.offline": "El ranking global no está disponible actualmente.",
  "submit.cta": "Enviar",
  "submit.ctaSignIn": "Iniciar sesión con Google",
  "submit.submitting": "Enviando…",
  "submit.dismiss": "Quizá luego",
  "submit.close": "Cerrar",
  "submit.error": "Error al enviar. Inténtalo de nuevo en breve.",
  "gameover.submit": "🏆 Enviar puntuación",
  "profile.nicknameEmpty": "Introduce un apodo.",
  "profile.nicknameTooLong": "El apodo debe tener 20 caracteres o menos.",
  "profile.nicknameError": "No se pudo actualizar el apodo.",
  "leaderboard.mergesShort": "{n}m",
};

const fr: Catalog = {
  "score.label": "Score ·",
  "score.high": "Meilleur ·",
  "queue.next": "Suivant",
  "integrity.label": "Intégrité de la Terre",
  "pause.label": "En pause",
  "goal.label": "🎯 Objectif · Forger une étoile",
  "goal.peak": "Palier max {current} / {total}",
  "goal.peakDot": "Palier max · {current} / {total}",
  "combo.line": "Combo ×{n} · {m} chaîne",
  "breach.danger": "DANGER !",
  "breach.atmosphere": "⚠ Brèche atmosphérique !",
  "breach.escape": "🌌 Orbite perdue !",
  "gameover.shattered.title": "💥 Terre brisée",
  "gameover.shattered.sub":
    "Trop de débris ont franchi l'atmosphère — la Terre s'est effondrée.",
  "gameover.shattered.cta": "Recommencer",
  "gameover.star.title": "🌟 Étoile forgée",
  "gameover.star.sub":
    "Vous avez forgé une étoile ! Mode infini débloqué — visez le meilleur score.",
  "gameover.star.cta": "Continuer",
  "gameover.final": "Final · {value}",
  "gameover.high": "Record · {value}",
  "tutorial.skip": "Passer",
  "tutorial.next": "Suivant",
  "tutorial.start": "Commencer !",
  "tutorial.step.launch.title": "🌌 Tir",
  "tutorial.step.launch.body":
    "Appuyez, tirez à l'opposé de la cible puis relâchez — la météorite part en sens inverse de la fronde. Plus vous tirez, plus elle va loin.",
  "tutorial.step.merge.title": "✨ Fusion",
  "tutorial.step.merge.body":
    "Deux météorites du même palier fusionnent en passant au suivant. Éclat → Petite météorite → Météorite → … → Étoile !",
  "tutorial.step.danger.title": "⚠ Attention",
  "tutorial.step.danger.body":
    "Les météorites laissées hors de l'atmosphère pointillée endommagent la Terre — plus elles sont grosses, plus c'est grave. La Lune en orbite lente peut aussi couper la trajectoire.",
  "tutorial.step.goal.title": "🎯 Objectif · Forger une étoile",
  "tutorial.step.goal.body":
    "Atteignez le palier 9 pour gagner ! Protégez la Terre et battez le record. Bonne chance ✨",
  "tier.meteor-shard.name": "Éclat de météorite",
  "tier.small-meteor.name": "Petite météorite",
  "tier.meteor.name": "Météorite",
  "tier.comet.name": "Comète",
  "tier.asteroid.name": "Astéroïde",
  "tier.moon.name": "Lune",
  "tier.planet.name": "Planète",
  "tier.protostar.name": "Protoétoile",
  "tier.star.name": "Étoile",
  "auth.signin": "Se connecter avec Google",
  "auth.signout": "Déconnexion",
  "leaderboard.title": "🏆 Classement mondial",
  "leaderboard.refresh": "Actualiser",
  "leaderboard.close": "Fermer",
  "leaderboard.openButton": "Classement",
  "leaderboard.empty": "Aucun score pour le moment.",
  "leaderboard.loading": "Chargement du classement…",
  "leaderboard.offline": "Le classement mondial est hors ligne — seuls les records locaux s'affichent.",
  "leaderboard.error": "Impossible de charger le classement.",
  "leaderboard.yourRank": "Votre rang",
  "submit.headline": "{value} points — soumettre au classement mondial ?",
  "submit.headlineStar": "🌟 Étoile forgée ! Soumettre {value} points au classement mondial ?",
  "submit.signedIn": "Soumis en tant que {name}",
  "submit.signedOut": "Connectez-vous avec Google pour enregistrer votre score.",
  "submit.offline": "Le classement mondial est actuellement indisponible.",
  "submit.cta": "Envoyer",
  "submit.ctaSignIn": "Se connecter avec Google",
  "submit.submitting": "Envoi…",
  "submit.dismiss": "Plus tard",
  "submit.close": "Fermer",
  "submit.error": "Échec de l'envoi. Réessayez dans un instant.",
  "gameover.submit": "🏆 Soumettre le score",
  "profile.nicknameEmpty": "Entrez un pseudo.",
  "profile.nicknameTooLong": "Le pseudo doit comporter au plus 20 caractères.",
  "profile.nicknameError": "Échec de la mise à jour du pseudo.",
  "leaderboard.mergesShort": "{n}m",
};

const de: Catalog = {
  "score.label": "Punkte ·",
  "score.high": "Bestwert ·",
  "queue.next": "Nächster",
  "integrity.label": "Erd-Integrität",
  "pause.label": "Pausiert",
  "goal.label": "🎯 Ziel · Erschaffe einen Stern",
  "goal.peak": "Höchste Stufe {current} / {total}",
  "goal.peakDot": "Höchste Stufe · {current} / {total}",
  "combo.line": "Combo ×{n} · {m} Kette",
  "breach.danger": "GEFAHR!",
  "breach.atmosphere": "⚠ Atmosphären-Bruch!",
  "breach.escape": "🌌 Orbit verloren!",
  "gameover.shattered.title": "💥 Erde zerstört",
  "gameover.shattered.sub":
    "Zu viel Geröll hat die Ozonschicht überschritten — die Erde ist eingestürzt.",
  "gameover.shattered.cta": "Neustart",
  "gameover.star.title": "🌟 Stern entstanden",
  "gameover.star.sub":
    "Du hast einen Stern erschaffen! Endlosmodus aktiv — jage den Highscore.",
  "gameover.star.cta": "Weiter",
  "gameover.final": "Final · {value}",
  "gameover.high": "Bestwert · {value}",
  "tutorial.skip": "Überspringen",
  "tutorial.next": "Weiter",
  "tutorial.start": "Start!",
  "tutorial.step.launch.title": "🌌 Abschuss",
  "tutorial.step.launch.body":
    "Drücken, vom Ziel weg ziehen und loslassen — der Meteor fliegt entgegen der Schleuderrichtung. Je stärker, desto weiter.",
  "tutorial.step.merge.title": "✨ Verschmelzen",
  "tutorial.step.merge.body":
    "Zwei Meteore derselben Stufe stoßen und verschmelzen zur nächsten. Splitter → Kleiner Meteor → Meteor → … → Stern!",
  "tutorial.step.danger.title": "⚠ Vorsicht",
  "tutorial.step.danger.body":
    "Meteore außerhalb der gestrichelten Atmosphäre schaden der Erde — größere noch mehr. Auch der langsam kreisende Mond kann den Schuss blockieren.",
  "tutorial.step.goal.title": "🎯 Ziel · Sternengeburt",
  "tutorial.step.goal.body":
    "Erreiche Stufe 9, um zu gewinnen! Rette die Erde und jage den Highscore. Viel Glück ✨",
  "tier.meteor-shard.name": "Meteor-Splitter",
  "tier.small-meteor.name": "Kleiner Meteor",
  "tier.meteor.name": "Meteor",
  "tier.comet.name": "Komet",
  "tier.asteroid.name": "Asteroid",
  "tier.moon.name": "Mond",
  "tier.planet.name": "Planet",
  "tier.protostar.name": "Protostern",
  "tier.star.name": "Stern",
  "auth.signin": "Mit Google anmelden",
  "auth.signout": "Abmelden",
  "leaderboard.title": "🏆 Globale Bestenliste",
  "leaderboard.refresh": "Aktualisieren",
  "leaderboard.close": "Schließen",
  "leaderboard.openButton": "Rangliste",
  "leaderboard.empty": "Noch keine Punkte eingetragen.",
  "leaderboard.loading": "Rangliste wird geladen…",
  "leaderboard.offline": "Globale Bestenliste offline — nur lokale Rekorde sichtbar.",
  "leaderboard.error": "Rangliste konnte nicht geladen werden.",
  "leaderboard.yourRank": "Dein Platz",
  "submit.headline": "{value} Punkte — auf der globalen Bestenliste eintragen?",
  "submit.headlineStar": "🌟 Stern erschaffen! {value} Punkte global eintragen?",
  "submit.signedIn": "Eintragung als {name}",
  "submit.signedOut": "Melde dich mit Google an, um deine Punkte zu speichern.",
  "submit.offline": "Globale Bestenliste derzeit nicht verfügbar.",
  "submit.cta": "Eintragen",
  "submit.ctaSignIn": "Mit Google anmelden",
  "submit.submitting": "Wird eingetragen…",
  "submit.dismiss": "Später",
  "submit.close": "Schließen",
  "submit.error": "Eintragung fehlgeschlagen. Bitte später erneut versuchen.",
  "gameover.submit": "🏆 Punkte eintragen",
  "profile.nicknameEmpty": "Bitte gib einen Spitznamen ein.",
  "profile.nicknameTooLong": "Spitzname darf höchstens 20 Zeichen haben.",
  "profile.nicknameError": "Spitzname konnte nicht aktualisiert werden.",
  "leaderboard.mergesShort": "{n}m",
};

const ptBR: Catalog = {
  "score.label": "Pontos ·",
  "score.high": "Recorde ·",
  "queue.next": "Próximo",
  "integrity.label": "Integridade da Terra",
  "pause.label": "Pausado",
  "goal.label": "🎯 Objetivo · Forje uma Estrela",
  "goal.peak": "Maior nível {current} / {total}",
  "goal.peakDot": "Maior nível · {current} / {total}",
  "combo.line": "Combo ×{n} · {m} em sequência",
  "breach.danger": "PERIGO!",
  "breach.atmosphere": "⚠ Atmosfera rompida!",
  "breach.escape": "🌌 Órbita perdida!",
  "gameover.shattered.title": "💥 Terra destruída",
  "gameover.shattered.sub":
    "Muitos destroços passaram da ozonosfera — a Terra colapsou.",
  "gameover.shattered.cta": "Reiniciar",
  "gameover.star.title": "🌟 Estrela forjada",
  "gameover.star.sub":
    "Você forjou uma estrela! Modo infinito liberado — vá atrás do recorde.",
  "gameover.star.cta": "Continuar",
  "gameover.final": "Final · {value}",
  "gameover.high": "Recorde · {value}",
  "tutorial.skip": "Pular",
  "tutorial.next": "Próximo",
  "tutorial.start": "Começar!",
  "tutorial.step.launch.title": "🌌 Lançamento",
  "tutorial.step.launch.body":
    "Pressione, arraste no sentido oposto ao alvo e solte — o meteoro voa no sentido contrário ao estilingue. Quanto mais puxar, mais longe.",
  "tutorial.step.merge.title": "✨ Fusão",
  "tutorial.step.merge.body":
    "Dois meteoros do mesmo nível colidem e viram o próximo. Fragmento → Pequeno → Meteoro → … → Estrela!",
  "tutorial.step.danger.title": "⚠ Atenção",
  "tutorial.step.danger.body":
    "Meteoros parados fora da atmosfera tracejada danificam a Terra — quanto maiores, pior. A Lua em órbita lenta também pode bloquear o tiro.",
  "tutorial.step.goal.title": "🎯 Objetivo · Forjar uma estrela",
  "tutorial.step.goal.body":
    "Chegue ao nível 9 para vencer! Proteja a Terra e mire no recorde. Boa sorte ✨",
  "tier.meteor-shard.name": "Fragmento",
  "tier.small-meteor.name": "Meteoro pequeno",
  "tier.meteor.name": "Meteoro",
  "tier.comet.name": "Cometa",
  "tier.asteroid.name": "Asteroide",
  "tier.moon.name": "Lua",
  "tier.planet.name": "Planeta",
  "tier.protostar.name": "Protoestrela",
  "tier.star.name": "Estrela",
  "auth.signin": "Entrar com Google",
  "auth.signout": "Sair",
  "leaderboard.title": "🏆 Ranking global",
  "leaderboard.refresh": "Atualizar",
  "leaderboard.close": "Fechar",
  "leaderboard.openButton": "Ranking",
  "leaderboard.empty": "Ainda não há pontuações.",
  "leaderboard.loading": "Carregando ranking…",
  "leaderboard.offline": "O ranking global está offline — só os recordes locais aparecem.",
  "leaderboard.error": "Não foi possível carregar o ranking.",
  "leaderboard.yourRank": "Sua posição",
  "submit.headline": "{value} pontos — enviar para o ranking global?",
  "submit.headlineStar": "🌟 Estrela forjada! Enviar {value} pontos ao ranking global?",
  "submit.signedIn": "Enviar como {name}",
  "submit.signedOut": "Entre com Google para registrar sua pontuação.",
  "submit.offline": "O ranking global está indisponível no momento.",
  "submit.cta": "Enviar",
  "submit.ctaSignIn": "Entrar com Google",
  "submit.submitting": "Enviando…",
  "submit.dismiss": "Mais tarde",
  "submit.close": "Fechar",
  "submit.error": "Falha ao enviar. Tente novamente em instantes.",
  "gameover.submit": "🏆 Enviar pontuação",
  "profile.nicknameEmpty": "Digite um apelido.",
  "profile.nicknameTooLong": "O apelido deve ter até 20 caracteres.",
  "profile.nicknameError": "Falha ao atualizar o apelido.",
  "leaderboard.mergesShort": "{n}m",
};

const ru: Catalog = {
  "score.label": "Очки ·",
  "score.high": "Рекорд ·",
  "queue.next": "Дальше",
  "integrity.label": "Целостность Земли",
  "pause.label": "Пауза",
  "goal.label": "🎯 Цель · Создайте звезду",
  "goal.peak": "Макс. уровень {current} / {total}",
  "goal.peakDot": "Макс. уровень · {current} / {total}",
  "combo.line": "Комбо ×{n} · цепочка {m}",
  "breach.danger": "ОПАСНОСТЬ!",
  "breach.atmosphere": "⚠ Прорыв атмосферы!",
  "breach.escape": "🌌 Орбита потеряна!",
  "gameover.shattered.title": "💥 Земля разрушена",
  "gameover.shattered.sub":
    "Слишком много обломков прошло за озон — Земля рухнула.",
  "gameover.shattered.cta": "Заново",
  "gameover.star.title": "🌟 Звезда рождена",
  "gameover.star.sub":
    "Вы создали звезду! Бесконечный режим открыт — побейте рекорд.",
  "gameover.star.cta": "Продолжить",
  "gameover.final": "Итого · {value}",
  "gameover.high": "Рекорд · {value}",
  "tutorial.skip": "Пропустить",
  "tutorial.next": "Далее",
  "tutorial.start": "Старт!",
  "tutorial.step.launch.title": "🌌 Запуск",
  "tutorial.step.launch.body":
    "Нажмите, потяните в сторону, противоположную цели, и отпустите — метеорит летит в обратном направлении от рогатки. Чем сильнее потяните, тем дальше.",
  "tutorial.step.merge.title": "✨ Слияние",
  "tutorial.step.merge.body":
    "Два метеорита одного уровня сталкиваются и сливаются в следующий. Осколок → Малый → Метеорит → … → Звезда!",
  "tutorial.step.danger.title": "⚠ Внимание",
  "tutorial.step.danger.body":
    "Метеориты, оставшиеся за пунктирной атмосферой, повреждают Землю — крупные сильнее. Медленно вращающаяся Луна тоже может перекрыть путь.",
  "tutorial.step.goal.title": "🎯 Цель · Рождение звезды",
  "tutorial.step.goal.body":
    "Достигните 9-го уровня, чтобы победить! Спасайте Землю и побейте рекорд. Удачи ✨",
  "tier.meteor-shard.name": "Осколок метеорита",
  "tier.small-meteor.name": "Малый метеорит",
  "tier.meteor.name": "Метеорит",
  "tier.comet.name": "Комета",
  "tier.asteroid.name": "Астероид",
  "tier.moon.name": "Луна",
  "tier.planet.name": "Планета",
  "tier.protostar.name": "Протозвезда",
  "tier.star.name": "Звезда",
  "auth.signin": "Войти через Google",
  "auth.signout": "Выйти",
  "leaderboard.title": "🏆 Глобальный рейтинг",
  "leaderboard.refresh": "Обновить",
  "leaderboard.close": "Закрыть",
  "leaderboard.openButton": "Рейтинг",
  "leaderboard.empty": "Очков пока нет.",
  "leaderboard.loading": "Загрузка рейтинга…",
  "leaderboard.offline": "Глобальный рейтинг недоступен — показаны только локальные записи.",
  "leaderboard.error": "Не удалось загрузить рейтинг.",
  "leaderboard.yourRank": "Ваше место",
  "submit.headline": "{value} очков — отправить в глобальный рейтинг?",
  "submit.headlineStar": "🌟 Звезда рождена! Отправить {value} очков в глобальный рейтинг?",
  "submit.signedIn": "Запись от имени {name}",
  "submit.signedOut": "Войдите через Google, чтобы сохранить результат.",
  "submit.offline": "Глобальный рейтинг сейчас недоступен.",
  "submit.cta": "Отправить",
  "submit.ctaSignIn": "Войти через Google",
  "submit.submitting": "Отправка…",
  "submit.dismiss": "Позже",
  "submit.close": "Закрыть",
  "submit.error": "Не удалось отправить. Повторите попытку позже.",
  "gameover.submit": "🏆 Отправить очки",
  "profile.nicknameEmpty": "Введите ник.",
  "profile.nicknameTooLong": "Ник должен быть не длиннее 20 символов.",
  "profile.nicknameError": "Не удалось обновить ник.",
  "leaderboard.mergesShort": "{n}м",
};

export const CATALOGS: Record<Locale, Catalog> = {
  ko,
  en,
  ja,
  "zh-CN": zhCN,
  "zh-TW": zhTW,
  es,
  fr,
  de,
  "pt-BR": ptBR,
  ru,
};

export const __testing = { ko, en, ja, zhCN, zhTW, es, fr, de, ptBR, ru };
